import React from 'react';
import {BaseSettingsForm} from '@/components/forms';
import {Button} from '@/components/ui';
import {Row, Col} from 'react-bootstrap';
import * as yup from 'yup';
import i18n from 'i18n';
import {buildStoryboardURL} from '@/utils/urlBuilder';

interface StoryboardData {
    title: string;
    id: number;
}

interface StoryboardSettingsFormProps {
    storyboardId: number;
    initialData: StoryboardData;
    onSave: (data: StoryboardData) => void;
}

const StoryboardSettingsFormSchema = yup.object({
    title: yup.string()
        .max(255, i18n.t('jt_storyboard_title_max_length.text'))
        .required(i18n.t('jt_required_field.text')),
});

const StoryboardSettingsForm: React.FC<StoryboardSettingsFormProps> = React.memo((
    {
        storyboardId,
        initialData,
        onSave,
    },
) => {
    const customSubmissionHandler = async (values: { title: string }) => {
        const response = await fetch(buildStoryboardURL('storyboardDetails', storyboardId), {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(values),
        });

        if (!response.ok) {
            throw new Error('Failed to update storyboard');
        }

        const data = await response.json();

        onSave({
            id: storyboardId,
            ...values,
        });

        return {data};
    };

    return (
        <BaseSettingsForm
            entityId={storyboardId}
            resourceUrl="" // Not used with customSubmissionHandler
            storeActionPath="" // Not used with customSubmissionHandler
            validationSchema={StoryboardSettingsFormSchema}
            initialValues={initialData}
            successMessageKey="jt_story_saved.text"
            customSubmissionHandler={customSubmissionHandler}
        >
            {(formik, loading) => (
                <div>
                    <div className="mb-3">
                        <label htmlFor="storyboardTitle" className="form-label">
                            {i18n.t('jt_storyboard_title.text')}
                        </label>
                        <input
                            type="text"
                            id="storyboardTitle"
                            name="title"
                            className={`form-control ${formik.touched.title && formik.errors.title ? 'is-invalid' : ''}`}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.title}
                            autoFocus
                            data-cy="storyboard-settings-title-input"
                        />
                        {formik.touched.title && formik.errors.title && (
                            <div className="invalid-feedback">
                                {formik.errors.title}
                            </div>
                        )}
                    </div>
                    
                    <Row className="mt-4">
                        <Col xs={12} className="d-flex justify-content-end">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={formik.isSubmitting}
                                loading={formik.isSubmitting}
                                data-cy="storyboard-settings-save-btn"
                            >
                                {storyboardId ? i18n.t('jt_update.text') : i18n.t('jt_save.text')}
                            </Button>
                        </Col>
                    </Row>
                </div>
            )}
        </BaseSettingsForm>
    );
});

export default StoryboardSettingsForm; 